import { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import debounce from 'lodash/debounce';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// @zillow / @zg-rentals
import { CometTheme } from '@zillow/zrs-comet';
import { getSensor as getPxSensor } from '@zg-rentals/perimeterx-browser';
import { BlockedUserModal } from '@zg-rentals/blocked-user-modal';
import { RentalsB2BLayout } from '@zg-rentals/b2b-layout';

// Actions
import AppActions from '../flux/actions/AppActions';

// Misc / Utils
import * as S from './AppStyles';
import { getDevice } from '../utils/browserUtils';
import getRoutes from '../utils/routes';
import { webVitals_initialize } from '../utils/webVitalsUtils';

// Types
import type { State } from '../types/State';

const applyRegLoginStyles = () => {
  // @ts-ignore
  import('@zillow/reg-login/src/less/reg-login.less');
};

const metaDescriptionContent =
  'Use Zillow Rental Manager to manage applications, screen tenants, collect rent and list ' +
  'your rental property on Zillow, Trulia and HotPads.';
const METAS = [{ name: 'description', content: metaDescriptionContent }];
const TITLE = 'How much should I charge for rent? – Rent Comp Estimator | Zillow Rental Manager';
const SCRIPTS = [{ type: 'text/javascript', innerHTML: getPxSensor() }];

const App = () => {
  // Redux
  const dispatch = useDispatch();
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const pathNameWithoutTrailingSlash = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const asPath = useSelector((state: State) => state.router.location.path);
  const user = useSelector((state: State) => state.user);

  const setDeviceAndWidth = debounce(() => {
    const device = getDevice();
    dispatch(AppActions.updateApp({ device }));
  }, 100);

  useEffect(() => {
    // Google Web Vitals tracking
    webVitals_initialize();
    setDeviceAndWidth();
    window.addEventListener('resize', setDeviceAndWidth);
  }, []);

  useEffect(applyRegLoginStyles, []);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 5 * (60 * 1000), // 5 mins
        useErrorBoundary: true,
        retry: 0,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={CometTheme}>
        <S.ResetStyles />
        <S.AppContainer>
          <Helmet meta={METAS} title={TITLE} script={SCRIPTS} />
          <RentalsB2BLayout router={{ asPath, pathname: pathNameWithoutTrailingSlash }}>{getRoutes()}</RentalsB2BLayout>
          {user?.restrictions?.isAllowed === false && <BlockedUserModal restrictions={user?.restrictions} />}
        </S.AppContainer>
        <S.GlobalStyle />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
